import { TabsContent } from '@/components/molecules';
import { Heading, Paragraph, Button, Switch } from '@/components/atoms';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { useUpdateUser, useUser } from '@/api';

interface NotificationFormData {
  allowInAppNotifications: boolean;
  allowEmailNotifications: boolean;
  allowSmsNotifications: boolean;
}

export const NotificationTab = () => {
  const { data: user } = useUser('me');

  const [formData, setFormData] = useState<NotificationFormData>({
    allowInAppNotifications: false,
    allowEmailNotifications: false,
    allowSmsNotifications: false,
  });

  useEffect(() => {
    if (user?.settings) {
      setFormData({
        allowInAppNotifications: user.settings.allowInAppNotifications,
        allowEmailNotifications: user.settings.allowEmailNotifications,
        allowSmsNotifications: user.settings.allowSmsNotifications,
      });
    }
  }, [user]);

  const { mutate: updateUser, isPending } = useUpdateUser(
    user?.id?.toString() || 'me'
  );

  const { handleSubmit } = useForm<NotificationFormData>();

  const onSubmit = () => {
    updateUser({
      settings: formData,
    });
  };

  const handleToggle = (field: keyof NotificationFormData) => {
    setFormData((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <TabsContent value="notification">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6 max-w-xl mx-auto py-6"
      >
        <div className="flex flex-col gap-8">
          <div className="flex gap-2 items-center">
            <div className="flex flex-col gap-2 w-full">
              <Heading size="sm">In-App Notifications</Heading>
              <Paragraph className="text-muted-foreground" size="sm">
                Receive notifications within the app
              </Paragraph>
            </div>
            <Switch
              checked={formData.allowInAppNotifications}
              onCheckedChange={() => handleToggle('allowInAppNotifications')}
            />
          </div>

          <div className="flex gap-2 items-center">
            <div className="flex flex-col gap-2 w-full">
              <Heading size="sm">Email Notifications</Heading>
              <Paragraph className="text-muted-foreground" size="sm">
                Get notifications sent to your email address
              </Paragraph>
            </div>
            <Switch
              checked={formData.allowEmailNotifications}
              onCheckedChange={() => handleToggle('allowEmailNotifications')}
            />
          </div>

          <div className="flex gap-2 items-center">
            <div className="flex flex-col gap-2 w-full">
              <Heading size="sm">SMS Notifications</Heading>
              <Paragraph className="text-muted-foreground" size="sm">
                Receive notifications via SMS to your phone
              </Paragraph>
            </div>
            <Switch
              checked={formData.allowSmsNotifications}
              onCheckedChange={() => handleToggle('allowSmsNotifications')}
            />
          </div>
        </div>

        <Button
          className="w-full"
          label="Save"
          size="lg"
          type="submit"
          isLoading={isPending}
        />
      </form>
    </TabsContent>
  );
};
